/**
 * Mobile Box Score Categories Component
 * Shows hitting and pitching stats with league rankings.
 *
 * Bible § 14 stat-column rule (ratified 2026-05-12): the tile set is derived
 * from league_settings.scoring_categories. Each scoring-category abbreviation
 * maps to the rankings-dictionary key + the totals-field-name + the display
 * label via TILE_CONFIG below.
 */

import React, { useState } from 'react';
import { dynastyTheme, withAlpha } from '../../../services/colorService';
import MobileRankingsModal from './MobileRankingsModal';

// Maps scoring-category abbreviation → tile config.
// rankKey:     key in the rankings dictionary returned by the backend
// totalsField: key in the {hittingTotals,pitchingTotals} props
// label:       display label on the tile
// rateFallback: empty-state string for rate stats
const HITTING_TILE_CONFIG = {
  'R':   { rankKey: 'total_runs', totalsField: 'r',   label: 'Runs' },
  'HR':  { rankKey: 'total_hr',   totalsField: 'hr',  label: 'HR' },
  'RBI': { rankKey: 'total_rbi',  totalsField: 'rbi', label: 'RBI' },
  'SB':  { rankKey: 'total_sb',   totalsField: 'sb',  label: 'SB' },
  'AVG': { rankKey: 'avg',        totalsField: 'avg', label: 'AVG', rateFallback: '.000' },
  'OPS': { rankKey: 'ops',        totalsField: 'ops', label: 'OPS', rateFallback: '.000' },
  'OBP': { rankKey: 'obp',        totalsField: 'obp', label: 'OBP', rateFallback: '.000' },
  'SLG': { rankKey: 'slg',        totalsField: 'slg', label: 'SLG', rateFallback: '.000' },
  'BB':  { rankKey: 'total_bb',   totalsField: 'bb',  label: 'BB' },
  'K':   { rankKey: 'total_so',   totalsField: 'so',  label: 'K' },
};

const PITCHING_TILE_CONFIG = {
  'W':    { rankKey: 'total_wins',  totalsField: 'w',    label: 'Wins' },
  'SV':   { rankKey: 'total_saves', totalsField: 'sv',   label: 'Saves' },
  'ERA':  { rankKey: 'era',         totalsField: 'era',  label: 'ERA',  rateFallback: '0.00' },
  'WHIP': { rankKey: 'whip',        totalsField: 'whip', label: 'WHIP', rateFallback: '0.000' },
  'SO':   { rankKey: 'total_k',     totalsField: 'so',   label: 'SO' },
  'QS':   { rankKey: 'total_qs',    totalsField: 'qs',   label: 'QS' },
  'HD':   { rankKey: 'total_hld',   totalsField: 'hd',   label: 'HLD' },
  'HLD':  { rankKey: 'total_hld',   totalsField: 'hld',  label: 'HLD' },
  'BS':   { rankKey: 'total_bs',    totalsField: 'bs',   label: 'BS' },
  'IP':   { rankKey: 'total_ip',    totalsField: 'ip',   label: 'IP' },
  'L':    { rankKey: 'total_losses', totalsField: 'l',   label: 'L' },
};

const buildTiles = (scoring, totals, configMap) => (
  (scoring || [])
    .map((abbr) => configMap[abbr])
    .filter(Boolean)
    .map((cfg) => ({
      key: cfg.rankKey,
      label: cfg.label,
      value: totals?.[cfg.totalsField] ?? (cfg.rateFallback ?? 0),
      rankKey: cfg.rankKey,
    }))
);

const BoxScoreCategories = ({
  league,
  rankings,
  hittingTotals,
  pitchingTotals,
  allRankings = null,
  yourTeamId = null,
  statsDate = '',
  onTileClick = null,
  // Chunk 3.14 — tile-enrichment props (Variant B: micro-sparkline + delta).
  weeklyHistory = null,   // { rankKey: [w1..w6] } — null cells = no-data
  wow = null,             // { rankKey: { delta, direction } }
  weekly = null,          // { rankKey: weekly total } — for bottom-sheet
  lastWeek = null,        // { rankKey: last-week total } — for bottom-sheet
  season = null,          // { rankKey: season total } — for bottom-sheet
  weekMeta = null,        // week boundary metadata — for bottom-sheet caption
}) => {
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('total_hr');

  // Handler to open modal with a specific stat category
  const handleTileClick = (category) => {
    if (!allRankings) return; // Don't open if no data
    setSelectedCategory(category);
    setIsModalOpen(true);
    // Call parent handler if provided
    if (onTileClick) {
      onTileClick(category);
    }
  };
  // Helper: Get ordinal suffix (1st, 2nd, 3rd, etc.)
  const getRankSuffix = (num) => {
    if (num % 10 === 1 && num !== 11) return 'st';
    if (num % 10 === 2 && num !== 12) return 'nd';
    if (num % 10 === 3 && num !== 13) return 'rd';
    return 'th';
  };

  // (getRankStyling removed — the boxed-pill tier badge it built is gone; the
  // flush RankRow uses the inline `tierTextColor` helper in renderScrollLayout.
  // The tier/tierGlow color tokens remain centrally defined in colorService.)

  // Define stat categories — driven by league_settings.scoring_categories.
  // Unknown abbreviations (not in HITTING_TILE_CONFIG / PITCHING_TILE_CONFIG)
  // are silently dropped so unsupported categories don't render broken tiles.
  const scoring = league?.league_settings?.scoring_categories || {};
  const hittingStats = buildTiles(scoring.hitting, hittingTotals, HITTING_TILE_CONFIG);
  const pitchingStats = buildTiles(scoring.pitching, pitchingTotals, PITCHING_TILE_CONFIG);

  // OPTION 1: Horizontal Scroll — flush RankRow columns (RankRow.dc.html v1).
  const renderScrollLayout = () => {
    const C = dynastyTheme.tokens.colors;
    // Tier-colored rank TEXT (no filled pill): 1st gold / 2nd silver / 3rd bronze
    // / last blue / mid muted.
    const tierTextColor = (rank) => {
      if (!rank) return C.muted;
      if (rank.is_first) return C.primary;
      if (rank.rank === 2) return C.tier.silver;
      if (rank.rank === 3) return C.tier.bronze;
      if (rank.is_last) return C.info;
      return C.muted;
    };

    const CategoryCard = ({ stat, rank }) => {
      // Flush borderless column: label · big value · plain rank text · sparkline.
      return (
        <div
          onClick={() => handleTileClick(stat.rankKey)}
          style={{
            width: '76px',
            flex: 'none',
            padding: '5px 8px',
            borderRight: `1.5px solid ${withAlpha(C.slate, 0.75)}`,
            cursor: allRankings ? 'pointer' : 'default',
          }}
        >
          {/* Label */}
          <div style={{
            fontSize: '8px',
            fontWeight: '700',
            color: C.muted,
            textTransform: 'uppercase',
            letterSpacing: '0.3px'
          }}>
            {stat.label}
          </div>

          {/* Value */}
          <div style={{
            fontFamily: "'Lato', sans-serif",
            fontSize: '15px',
            fontWeight: '900',
            color: C.white,
            lineHeight: '1.05',
            fontVariantNumeric: 'tabular-nums'
          }}>
            {stat.value}
          </div>

          {/* Rank line — plain tier-colored text, no pill */}
          {rank && (
            <div style={{ fontSize: '9px', fontWeight: '800', color: tierTextColor(rank), fontVariantNumeric: 'tabular-nums' }}>
              {rank.rank}{getRankSuffix(rank.rank)}
              <span style={{ color: C.muted, fontWeight: '600' }}>/{rank.total_teams}</span>
            </div>
          )}

          {/* Chunk 3.14 Variant B — micro-sparkline (~56×12px) + delta trend.
              null history points render as polyline gaps. Direction
              ('up'|'down'|'flat'|'none') drives color. */}
          {(weeklyHistory?.[stat.rankKey] || wow?.[stat.rankKey]) && (() => {
            const hist = weeklyHistory?.[stat.rankKey] || [];
            const w = wow?.[stat.rankKey] || { delta: null, direction: 'none' };
            const dirColor =
              w.direction === 'up'   ? dynastyTheme.tokens.colors.spark.up :
              w.direction === 'down' ? dynastyTheme.tokens.colors.spark.down :
              w.direction === 'flat' ? dynastyTheme.tokens.colors.spark.flat :
              dynastyTheme.tokens.colors.spark.none;

            // Build segmented polylines for null-gapped data
            const xs = [0, 12, 24, 36, 48, 60];
            const numeric = hist.filter(v => v !== null && v !== undefined).map(Number);
            const minV = numeric.length ? Math.min(...numeric) : 0;
            const maxV = numeric.length ? Math.max(...numeric) : 1;
            const range = (maxV - minV) || 1;
            const ys = hist.map(v => (v === null || v === undefined) ? null : 12 - ((v - minV) / range) * 10);
            const segments = [];
            let current = [];
            for (let i = 0; i < Math.min(6, ys.length); i++) {
              if (ys[i] === null) {
                if (current.length >= 2) segments.push(current);
                current = [];
              } else {
                current.push(`${xs[i]},${ys[i]}`);
              }
            }
            if (current.length >= 2) segments.push(current);
            const lastIdx = ys.map((y, i) => y !== null ? i : -1).filter(i => i >= 0).pop();
            const lastX = lastIdx != null ? xs[lastIdx] : null;
            const lastY = lastIdx != null ? ys[lastIdx] : null;

            const isRate = ['avg', 'ops', 'era', 'whip'].includes(stat.rankKey);
            const fmtDelta = (d) => {
              if (d === null || d === undefined) return '—';
              if (isRate) {
                if (stat.rankKey === 'era') return `${d >= 0 ? '+' : ''}${Number(d).toFixed(2)}`;
                return `${d >= 0 ? '+' : ''}${Number(d).toFixed(3).replace(/^0\./, '.').replace(/^-0\./, '-.')}`;
              }
              return `${d >= 0 ? '+' : ''}${d}`;
            };
            const arrowGlyph =
              w.direction === 'up'   ? '▲' :
              w.direction === 'down' ? '▼' :
              w.direction === 'flat' ? '–' :
              '○';

            return (
              <div style={{ marginTop: '3px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <svg viewBox="0 0 60 14" preserveAspectRatio="none" style={{ width: '100%', height: '12px' }}>
                  {segments.map((seg, i) => (
                    <polyline
                      key={i}
                      points={seg.join(' ')}
                      fill="none"
                      stroke={withAlpha(dirColor, 0.5)}
                      strokeWidth="1.5"
                      strokeLinejoin="round"
                      strokeLinecap="round"
                    />
                  ))}
                  {numeric.length === 0 && (
                    <line x1="0" y1="7" x2="60" y2="7" stroke={dynastyTheme.tokens.colors.spark.baseline} strokeWidth="0.8" strokeDasharray="2,2" />
                  )}
                  {lastX !== null && lastY !== null && (
                    <circle cx={lastX} cy={lastY} r="1.4" fill={dirColor} />
                  )}
                </svg>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '3px',
                  color: dirColor,
                  fontSize: '9px',
                  fontWeight: '700',
                  fontVariantNumeric: 'tabular-nums',
                  lineHeight: 1,
                }}>
                  <span style={{ fontSize: '8px' }}>{arrowGlyph}</span>
                  <span>{fmtDelta(w.delta)}</span>
                </div>
              </div>
            );
          })()}
        </div>
      );
    };

    return (
      // No top margin: the section header's 6px bottom padding owns the gap to the
      // HITTING sub-label below (label→sub-label = 6px, symmetric with the 6px above
      // the label from the section wrapper).
      <div>
        {/* Hitting row */}
        <div style={{ marginBottom: '6px' }}>
          <div style={{ fontSize: '8px', fontWeight: '800', letterSpacing: '0.6px', textTransform: 'uppercase', color: C.neutral[500], lineHeight: 1, marginBottom: '2px' }}>
            Hitting
          </div>
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <div style={{ display: 'flex', width: 'max-content', borderTop: `1.5px solid ${withAlpha(C.slate, 0.75)}` }}>
              {hittingStats.map(stat => (
                <CategoryCard key={stat.key} stat={stat} rank={rankings?.[stat.rankKey]} />
              ))}
            </div>
          </div>
        </div>

        {/* Pitching row */}
        <div>
          <div style={{ fontSize: '8px', fontWeight: '800', letterSpacing: '0.6px', textTransform: 'uppercase', color: C.neutral[500], lineHeight: 1, marginBottom: '2px' }}>
            Pitching
          </div>
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <div style={{ display: 'flex', width: 'max-content', borderTop: `1.5px solid ${withAlpha(C.slate, 0.75)}` }}>
            {pitchingStats.map(stat => (
              <CategoryCard
                key={stat.key}
                stat={stat}
                rank={rankings?.[stat.rankKey]}
              />
            ))}
            </div>
          </div>
        </div>

        {/* Hide scrollbar CSS */}
        <style>{`
          div::-webkit-scrollbar {
            display: none;
          }
        `}</style>
      </div>
    );
  };

  return (
    <div>
      {renderScrollLayout()}
      
      {/* Rankings Modal — Chunk 3.14 extended with tile-enrichment props */}
      <MobileRankingsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        allRankings={allRankings}
        yourTeamId={yourTeamId}
        defaultCategory={selectedCategory}
        statsDate={statsDate}
        weekly={weekly}
        lastWeek={lastWeek}
        season={season}
        weeklyHistory={weeklyHistory}
        wow={wow}
        weekMeta={weekMeta}
      />
    </div>
  );
};

export default BoxScoreCategories;
